import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { io } from "socket.io-client";
import localforage from "localforage";
import { cfg } from "../runtimeConfig";

export interface NegotiationRTA {
  on(event: string, listener: (...args: any[]) => void): void;
  off(event: string, listener: (...args: any[]) => void): void;
  emit(event: string, ...payload: any[]): void;
  connect(): void;
  disconnect(): void;
}

const envMockFlag = process.env.REACT_APP_NEGOTIATE_MOCK_MODE === "true";
const configMockFlag = cfg.REACT_APP_NEGOTIATE_MOCK_MODE === "true";
const defaultMockMode = envMockFlag || configMockFlag;

type MockCard = {
  transferFeeCents?: number;
  id: string;
  username: string;
  position: number;
  lastBidAmount: number;
  lastBidOrg: string;
  lastBidDiff: number;
  avatar: string;
  orgAvatar: string;
  lastBidDate: string;
  orgTax: number;
};

// The shapes below are what enterprise-ads-api writes, field for field. The
// controller keeps its bookkeeping on plain numbers and only these leave the
// mock, so the tutorial meets the nulls and omitted keys production sends: the
// 2026-10-05 outage shipped because every double used 0, "" and arrays that
// were always present.

// model/negotiate.go ListHallPostsCardsItemResp. A post nobody has bid on in
// the cycle has no bid state, so its bid fields are null and its date is Go's
// zero time. avatar and orgAvatar are omitempty.
type ListHallPostsCardsItemResp = {
  transferFeeCents?: number;
  id: string;
  username: string;
  lastBidAmount: number | null;
  position: number;
  orgTax: number | null;
  lastBidDate: string;
  orgAvatar?: string;
  lastBidOrg: string | null;
  avatar?: string;
};

// model/negotiate.go ListHallPostsResp. Every highlight id and the
// power-offensive list are omitempty, so an empty board is `{"cards": null}`.
type ListHallPostsResp = {
  top1Id?: string;
  top2Id?: string;
  top3Id?: string;
  flop1Id?: string;
  flop2Id?: string;
  flop3Id?: string;
  powerOffensiveIds?: string[];
  cards: ListHallPostsCardsItemResp[] | null;
};

// model/users.go HallPost, the live addNewBid payload. The bid handler fills
// only the bid fields, so username, avatar and position go out zeroed.
type HallPost = {
  transferFeeCents?: number;
  id: string;
  username: string;
  avatar: string;
  lastBidOrg: string;
  lastBidAmount: number;
  lastBidDiff: number;
  orgAvatar: string;
  position: number;
  orgTax: number;
  lastBidDate: string;
};

// The tutorial uses the published initial send-fee parameters; no money is moved.
const mockTransferFee = (bidBRL: number) =>
  Math.max(30 + Math.ceil((bidBRL * 100 * 290) / 10_000), 50);
const UNBID_LAST_BID_DATE = "0001-01-01T00:00:00Z";

function mockBidFailure(
  status: number,
  reason?: string,
  message = reason,
  recordedOutcome = false,
) {
  return Object.assign(new Error(message), {
    response: {
      status,
      data: {
        error: true,
        reason,
        msg: message,
        ...(recordedOutcome ? { recordedOutcome: true } : {}),
      },
    },
  });
}

// unixToRFC3339 in the ads-api: UTC to the second, no fraction.
const toRFC3339Seconds = (iso: string) => iso.replace(/\.\d+Z$/, "Z");

function toHallPostsCard(card: MockCard): ListHallPostsCardsItemResp {
  const avatar = card.avatar ? { avatar: card.avatar } : {};
  // emitNewBid stamps a date on every bid it writes, so a card without one has
  // no bid in the cycle.
  if (!card.lastBidDate) {
    return {
      id: card.id,
      username: card.username,
      lastBidAmount: null,
      position: card.position,
      orgTax: null,
      lastBidDate: UNBID_LAST_BID_DATE,
      lastBidOrg: null,
      ...avatar,
    };
  }
  return {
    id: card.id,
    username: card.username,
    lastBidAmount: card.lastBidAmount,
    position: card.position,
    orgTax: card.orgTax,
    transferFeeCents: card.transferFeeCents,
    lastBidDate: toRFC3339Seconds(card.lastBidDate),
    ...(card.orgAvatar ? { orgAvatar: card.orgAvatar } : {}),
    lastBidOrg: card.lastBidOrg,
    ...avatar,
  };
}

function toHallPost(card: MockCard): HallPost {
  return {
    id: card.id,
    username: "",
    avatar: "",
    lastBidOrg: card.lastBidOrg,
    lastBidAmount: card.lastBidAmount,
    lastBidDiff: card.lastBidDiff,
    orgAvatar: card.orgAvatar,
    position: 0,
    orgTax: card.orgTax,
    transferFeeCents: card.transferFeeCents,
    lastBidDate: toRFC3339Seconds(card.lastBidDate),
  };
}

type MockSessionInfo = {
  iss: string;
  org_id: string;
  exp: number;
};

type NegotiationPeriod = {
  period: "negotiate" | "closed";
  endAt: string;
};

const mockSessionInfo: MockSessionInfo = {
  iss: "mock-dakasa",
  org_id: "mock-org",
  exp: Math.floor(Date.now() / 1000) + 3600,
};

interface MockOrg {
  id: string;
  avatar: string;
}

export const mockOrgs: { [key: string]: MockOrg } = {
  "mock-org": {
    id: "mock-org",
    avatar: "https://api.dicebear.com/9.x/identicon/svg?seed=org-dakasa",
  },
  "org-alpha": {
    id: "org-alpha",
    avatar: "https://api.dicebear.com/7.x/shapes/svg?seed=org-alpha",
  },
  "org-bravo": {
    id: "org-bravo",
    avatar: "https://api.dicebear.com/7.x/shapes/svg?seed=org-bravo",
  },
};

const mockOrgMetadata = {
  name: "DaKasa LTDA",
  avatar: mockOrgs["mock-org"]?.avatar || "",
};

const mockCardSeeds: MockCard[] = [
  {
    id: "mock-card-cleitinho13",
    username: "cleitinho13",
    position: 1,
    lastBidAmount: 0,
    lastBidOrg: "",
    lastBidDiff: 0,
    avatar: "https://i.pravatar.cc/80?img=1",
    orgAvatar: "",
    lastBidDate: "",
    orgTax: 0,
  },
  {
    id: "mock-card-isika",
    username: "isika",
    position: 2,
    lastBidAmount: 0,
    lastBidOrg: "",
    lastBidDiff: 0,
    avatar: "https://i.pravatar.cc/80?img=2",
    orgAvatar: "",
    lastBidDate: "",
    orgTax: 0,
  },
  {
    id: "mock-card-jao_pantanal",
    username: "jao_pantanal",
    position: 3,
    lastBidAmount: 0,
    lastBidOrg: "",
    lastBidDiff: 0,
    avatar: "https://i.pravatar.cc/80?img=3",
    orgAvatar: "",
    lastBidDate: "",
    orgTax: 0,
  },
  {
    id: "mock-card-torin777",
    username: "torin777",
    position: 4,
    lastBidAmount: 0,
    lastBidOrg: "",
    lastBidDiff: 0,
    avatar: "https://i.pravatar.cc/80?img=4",
    orgAvatar: "",
    lastBidDate: "",
    orgTax: 0,
  },
  {
    id: "mock-card-leonidas_300",
    username: "leonidas_300",
    position: 5,
    lastBidAmount: 0,
    lastBidOrg: "",
    lastBidDiff: 0,
    avatar: "https://i.pravatar.cc/80?img=5",
    orgAvatar: "",
    lastBidDate: "",
    orgTax: 0,
  },
  {
    id: "mock-card-rupaula_lua",
    username: "rupaula_lua",
    position: 6,
    lastBidAmount: 0,
    lastBidOrg: "",
    lastBidDiff: 0,
    avatar: "https://i.pravatar.cc/80?img=6",
    orgAvatar: "",
    lastBidDate: "",
    orgTax: 0,
  },
  {
    id: "mock-card-o_milaneso",
    username: "o_milaneso",
    position: 7,
    lastBidAmount: 0,
    lastBidOrg: "",
    lastBidDiff: 0,
    avatar: "https://i.pravatar.cc/80?img=7",
    orgAvatar: "",
    lastBidDate: "",
    orgTax: 0,
  },
];

const mockRankingSeeds = [
  {
    orgAvatar: "https://api.dicebear.com/9.x/identicon/svg?seed=org-dakasa",
    orgName: "DaKasa LTDA",
    offensivePoints: 18,
    rankingPosition: 1,
    projectedBidDiscountPercent: 50,
    projectedMonopolyFeeWaived: true,
  },
  {
    orgAvatar: "https://api.dicebear.com/7.x/shapes/svg?seed=org-bravo",
    orgName: "Org Bravo",
    offensivePoints: 14,
    rankingPosition: 2,
    projectedBidDiscountPercent: 25,
    projectedMonopolyFeeWaived: true,
  },
  {
    orgAvatar: "https://api.dicebear.com/7.x/shapes/svg?seed=org-charlie",
    orgName: "Org Charlie",
    offensivePoints: 9,
    rankingPosition: 3,
    projectedBidDiscountPercent: 10,
    projectedMonopolyFeeWaived: true,
  },
  {
    orgAvatar: "https://api.dicebear.com/7.x/shapes/svg?seed=org-delta",
    orgName: "Org Delta",
    offensivePoints: 0,
    rankingPosition: 4,
  },
  {
    orgAvatar: "https://api.dicebear.com/7.x/shapes/svg?seed=org-echo",
    orgName: "Org Echo",
    offensivePoints: 0,
    rankingPosition: 5,
  },
  {
    orgAvatar: "https://api.dicebear.com/7.x/shapes/svg?seed=org-foxtrot",
    orgName: "Org Foxtrot",
    offensivePoints: 0,
    rankingPosition: 6,
  },
];

const mockHistorySeeds = [
  {
    winnerPostsPositions: [1, 2, 3],
    fallbackMinorPostsPositions: [4],
    offensiveAmountBefore: 8,
    offensiveAmountAfter: 18,
    powerMultiplier: 1,
    cycleCost: 350,
    taxCost: 40,
    bidDiscountPercent: 50,
    monopolyFeeWaived: true,
    grossCycleCost: 700,
    discountCost: 350,
    netCycleCost: 350,
    negotiatedAt: new Date(Date.now() - 2 * 3600 * 1000).toISOString(),
  },
  {
    winnerPostsPositions: [1, 4, 6],
    fallbackMinorPostsPositions: [],
    offensiveAmountBefore: 0,
    offensiveAmountAfter: 8,
    powerMultiplier: 1,
    cycleCost: 125,
    taxCost: 32,
    negotiatedAt: new Date(Date.now() - 24 * 3600 * 1000).toISOString(),
  },
];

const mockPostDetails: Record<
  string,
  { postText: string; postMedia?: string; postMediaType?: string }
> = {
  "mock-card-cleitinho13": {
    postText: "Este é a postagem do usuário cleitinho13.",
  },
  "mock-card-isika": {
    postText: "Este é a postagem do usuário isika.",
    postMedia: "https://images.unsplash.com/photo-1501785888041-af3ef285b470",
    postMediaType: "image",
  },
  "mock-card-jao_pantanal": {
    postText: "Este é a postagem do usuário jao_pantanal.",
  },
  "mock-card-torin777": {
    postText: "Este é a postagem do usuário torin777.",
  },
  "mock-card-leonidas_300": {
    postText: "Este é a postagem do usuário leonidas_300.",
  },
  "mock-card-rupaula_lua": {
    postText: "Este é a postagem do usuário rupaula_lua.",
  },
  "mock-card-o_milaneso": {
    postText: "Este é a postagem do usuário o_milaneso.",
  },
};

/**
 * org_tax, exactly as the engine computes it.
 *
 * Line-by-line mirror of `calculateOrgTax` in
 * enterprise-ads-api/client/bid_lua.go. Two properties matter here and both were
 * absent from the previous mock:
 *
 *  - it is a MULTIPLIER on the bid (the panel and the biller both charge
 *    `bid * org_tax`), never an amount added to it;
 *  - it is 1 — no surcharge at all — for any org inside its fair share of the
 *    board. The tax is an anti-monopoly wall, not a fee on bidding.
 *
 * The old mock returned `Math.max(10, Math.round(value * 0.16))`: a percentage
 * shape used as a multiplier, with a floor of 10x, engaging on the very first
 * bid. That is what made a R$25 bid read as R$900 in the tutorial.
 */
const calculateOrgTax = (wins: number, totalCount: number, activeOrgsCount: number) => {
  if (totalCount <= 0) return 1;
  // No monopoly is possible in a market of one.
  if (activeOrgsCount <= 1) return 1;

  const share = wins / totalCount;
  const fair = 1 / activeOrgsCount;
  if (share <= fair) return 1;

  const excessRatio = (share - fair) / (1 - fair);
  const k = 4;
  const marketFactor = Math.log(activeOrgsCount + 1);
  const sizeFactor = totalCount < 10 ? Math.log(totalCount + 1) / Math.log(11) : 1;
  return Math.ceil(Math.exp(k * excessRatio * marketFactor * sizeFactor));
};

export class MockNegotiationController {
  private cards: MockCard[] = [];
  private openedPosts: string[] = [];
  private autoBuyEnabled = true;
  private sponsorMinorAudience = false;
  private marketPeriod: NegotiationPeriod = {
    period: "closed",
    endAt: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
  };
  private ranking = [...mockRankingSeeds];
  private history = [...mockHistorySeeds];
  private rta?: MockRTA;
  private session: MockSessionInfo;
  // Mirrors the cycle:<id>:active_orgs Redis SET: an org that ever placed a bid
  // above 1 stays in it for the whole cycle, even after being outbid. org_tax
  // reads its cardinality, so deriving it from the current leaders instead
  // would shrink the market back down and change the multiplier.
  private activeOrgs = new Set<string>();
  private recordedBids = new Map<
    string,
    {
      orgId: string;
      postId: string;
      amount: number;
      diff: number;
      result: HallPost | ReturnType<typeof mockBidFailure>;
    }
  >();

  constructor(
    private readonly seeds: MockCard[],
    session: MockSessionInfo,
  ) {
    this.session = session;
    this.reset();
  }

  attachRTA(rta: MockRTA) {
    this.rta = rta;
  }

  reset() {
    this.cards = this.seeds.map((card) => ({ ...card }));
    this.openedPosts = this.cards.slice(1, 2).map((card) => card.id);
    this.activeOrgs = new Set<string>();
    this.recordedBids.clear();
    this.autoBuyEnabled = true;
    this.sponsorMinorAudience = false;
    this.marketPeriod = {
      period: "closed",
      endAt: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
    };
    this.ranking = [...mockRankingSeeds];
    this.history = [...mockHistorySeeds];
  }

  setSessionInfo(session: MockSessionInfo) {
    this.session = session;
  }

  getHallResponse(): ListHallPostsResp {
    const response: ListHallPostsResp = {
      cards: this.cards.length > 0 ? this.cards.map(toHallPostsCard) : null,
    };
    // A slot with no card behind it is left out, never sent as "".
    const slots = [
      ["top1Id", 0],
      ["top2Id", 1],
      ["top3Id", 2],
      ["flop1Id", 4],
      ["flop2Id", 5],
      ["flop3Id", 6],
    ] as const;
    for (const [slot, index] of slots) {
      const id = this.cards[index]?.id;
      if (id) response[slot] = id;
    }
    const powerOffensiveId = this.cards[1]?.id;
    if (powerOffensiveId) response.powerOffensiveIds = [powerOffensiveId];
    return response;
  }

  getOpenedResponse() {
    return [...this.openedPosts];
  }

  getHistoryResponse() {
    return this.history.map((entry) => ({ ...entry }));
  }

  getRankingResponse() {
    return this.ranking.map((entry) => {
      if (entry.orgName === "DaKasa LTDA") {
        return {
          ...entry,
          orgName: mockOrgMetadata.name || entry.orgName,
          orgAvatar: mockOrgMetadata.avatar || entry.orgAvatar,
        };
      }
      return { ...entry };
    });
  }

  getOrgStatusResponse() {
    return {
      adultOnly: false,
      defaulter: false,
      offensiveAmount: 2,
      autoBuy: this.autoBuyEnabled,
      sponsorMinorAudience: this.sponsorMinorAudience,
      settlementMode: "prepaid",
      activeOrg: true,
      bidDiscountPercent: 0,
      monopolyFeeWaived: false,
      // Every company competes in the tour, DaKasa's own included.
      competitionExcluded: false,
    };
  }

  getCompetitionResponse() {
    const startsAt = new Date();
    const endsAt = new Date(startsAt);
    endsAt.setMonth(endsAt.getMonth() + 1);
    return {
      id: "00000000-0000-0000-0000-000000000101",
      status: "collecting",
      startsAt: startsAt.toISOString(),
      endsAt: endsAt.toISOString(),
      nextTransitionAt: endsAt.toISOString(),
      prizes: [
        { rank: 1, bidDiscountPercent: 50, monopolyFeeWaived: true },
        { rank: 2, bidDiscountPercent: 25, monopolyFeeWaived: true },
        { rank: 3, bidDiscountPercent: 10, monopolyFeeWaived: true },
      ],
    };
  }

  getMarketPeriodResponse() {
    return { ...this.marketPeriod };
  }

  setHistory(data: typeof mockHistorySeeds) {
    this.history = [...data];
  }

  setRanking(data: typeof mockRankingSeeds) {
    this.ranking = [...data];
  }

  openPost(postId: string) {
    if (!this.openedPosts.includes(postId)) {
      this.openedPosts.push(postId);
    }
  }

  setAutoBuy(enabled: boolean) {
    this.autoBuyEnabled = enabled;
    this.rta?.emit(`changeAutoBuyStatus-${this.session.org_id}`, { autoBuy: enabled });
    return this.getOrgStatusResponse();
  }

  setSponsorMinorAudience(enabled: boolean) {
    this.sponsorMinorAudience = enabled;
    this.rta?.emit(`negotiationNoticesChanged-${this.session.org_id}`, { cycleId: "tutorial" });
    return { enabled };
  }

  setMarketPeriod(period: NegotiationPeriod["period"], endAt?: string) {
    this.marketPeriod = {
      period,
      endAt: endAt || new Date(Date.now() + 15 * 60 * 1000).toISOString(),
    };
    this.rta?.emit("negotiateMarket", this.marketPeriod);
    return this.marketPeriod;
  }

  emitNewBid(postId?: string, overrides: Partial<MockCard> = {}) {
    const target = postId
      ? this.ensureCard(postId)
      : this.cards[Math.floor(Math.random() * this.cards.length)];
    if (!target) return null;
    const hasAmountOverride = typeof overrides.lastBidAmount === "number";
    const hasDiffOverride = typeof overrides.lastBidDiff === "number";

    if (hasAmountOverride) {
      target.lastBidAmount = overrides.lastBidAmount || 0;
    }
    if (hasDiffOverride) {
      target.lastBidDiff = overrides.lastBidDiff || 0;
      if (!hasAmountOverride) {
        target.lastBidAmount += overrides.lastBidDiff || 0;
      }
    }

    target.lastBidOrg = overrides.lastBidOrg || this.session.org_id;
    // A real tutorial session need not have a logo loaded yet. Changing the
    // leader must still clear the previous company's image, just as the live
    // producer broadcasts an empty avatar when its cosmetic lookup fails.
    target.orgAvatar = overrides.orgAvatar ?? mockOrgs[target.lastBidOrg]?.avatar ?? "";
    target.lastBidDate = new Date().toISOString();
    // Same order as bid_lua.go: the bid is written, the bidder joins the active
    // set, and only then is org_tax computed — so the bid being placed counts
    // itself, for the org that placed it.
    if (target.lastBidAmount > 1 && target.lastBidOrg) {
      this.activeOrgs.add(target.lastBidOrg);
    }
    target.orgTax = calculateOrgTax(
      this.winsFor(target.lastBidOrg),
      this.cards.length,
      this.activeOrgs.size,
    );
    target.transferFeeCents = mockTransferFee(target.lastBidAmount);
    const event = toHallPost(target);
    this.rta?.emit("addNewBid", event);
    return { ...event };
  }

  recordBid(postId: string, amount: number, diff: number) {
    const targetCard = postId ? this.ensureCard(postId) : undefined;
    const currentAmount = targetCard !== undefined ? targetCard.lastBidAmount : amount || 0;
    const finalAmount = currentAmount + (diff || 0);
    return this.emitNewBid(postId, {
      lastBidAmount: finalAmount,
      lastBidDiff: diff,
    });
  }

  recordBidOnce(postId: string, amount: number, diff: number, key?: string) {
    const recorded = key ? this.recordedBids.get(key) : undefined;
    if (recorded) {
      if (
        recorded.orgId !== this.session.org_id ||
        recorded.postId !== postId ||
        recorded.amount !== amount ||
        recorded.diff !== diff
      )
        throw mockBidFailure(409, "idempotency_key_reused");
      if (recorded.result instanceof Error) throw recorded.result;
      return recorded.result;
    }
    const refuse = (status: number, reason?: string, message = reason): never => {
      const result = mockBidFailure(status, reason, message, Boolean(key));
      if (key)
        this.recordedBids.set(key, { orgId: this.session.org_id, postId, amount, diff, result });
      throw result;
    };
    const target = this.ensureCard(postId);
    if (!target || !Number.isSafeInteger(diff) || diff <= 0)
      throw mockBidFailure(400, "invalid_bid");
    if (
      this.marketPeriod.period !== "negotiate" ||
      Date.parse(this.marketPeriod.endAt) <= Date.now()
    )
      refuse(409, "negotiation_closed");
    if (target.lastBidOrg === this.session.org_id) refuse(409, "same_org");
    if (Date.parse(target.lastBidDate) + 10_000 > Date.now())
      refuse(429, "wait", "wait 10 seconds to make a new bid");
    if (amount !== target.lastBidAmount + diff) refuse(409, "bid_moved");
    const result = this.recordBid(postId, amount, diff);
    if (key && result)
      this.recordedBids.set(key, { orgId: this.session.org_id, postId, amount, diff, result });
    return result;
  }

  /**
   * The `/org-tax-preview` answer for the calling org.
   *
   * Mirrors GetOrgTaxPreview (enterprise-ads-api/controllers/negotiate.go): the
   * bid being previewed does not exist yet, so `wins` starts at 1 and the
   * caller's org is counted as active if it is not in the set already. The bid
   * VALUE is deliberately unused — org_tax does not depend on how much you bid,
   * only on how much of the board you hold.
   */
  calculateTax(_value: number) {
    const active = this.activeOrgs.has(this.session.org_id)
      ? this.activeOrgs.size
      : this.activeOrgs.size + 1;
    return calculateOrgTax(this.winsFor(this.session.org_id) + 1, this.cards.length, active);
  }

  private winsFor(orgId: string) {
    if (!orgId) return 0;
    return this.cards.filter((card) => card.lastBidOrg === orgId).length;
  }

  private ensureCard(id: string) {
    return this.cards.find((card) => card.id === id);
  }
}

class MockRTA implements NegotiationRTA {
  private handlers = new Map<string, Set<(...args: any[]) => void>>();
  private interval?: ReturnType<typeof setInterval>;
  private connected = false;

  constructor(private controller: MockNegotiationController) {}

  on(event: string, listener: (...args: any[]) => void) {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)?.add(listener);
  }

  off(event: string, listener: (...args: any[]) => void) {
    this.handlers.get(event)?.delete(listener);
  }

  emit(event: string, ...payload: any[]) {
    const listeners = this.handlers.get(event);
    if (!listeners) return;
    listeners.forEach((listener) => listener(...payload));
  }

  connect() {
    if (this.connected) return;
    this.connected = true;
    this.controller.attachRTA(this);
  }

  disconnect() {
    if (!this.connected) return;
    this.connected = false;
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = undefined;
    }
  }

  private emitRandomBid() {
    this.controller.emitNewBid();
  }
}

class MockNegotiateAPI {
  defaults = {} as any;
  interceptors = {
    request: { use: () => {}, eject: () => {} },
    response: { use: () => {}, eject: () => {} },
  };

  constructor(private controller: MockNegotiationController) {}

  async get(url: string, config?: AxiosRequestConfig) {
    const normalized = url.replace(/^\//, "");
    if (normalized.startsWith("org-tax-preview")) {
      const value = (config?.params as any)?.value ?? 0;
      const orgTax = this.controller.calculateTax(Number(value));
      return {
        data: {
          orgTax,
          calculatedOrgTax: orgTax,
          bidDiscountPercent: 0,
          monopolyFeeWaived: false,
          chargeAmountCents: Number(value) * orgTax * 100 + mockTransferFee(Number(value)),
          transferFeeCents: mockTransferFee(Number(value)),
        },
      };
    }

    if (normalized.startsWith("post/")) {
      const id = normalized.split("/").pop() as string;
      return {
        data: mockPostDetails[id] || {
          postText: "Conteúdo de mock indefinido.",
        },
      };
    }

    switch (normalized) {
      case "opened":
        return { data: this.controller.getOpenedResponse() };
      case "negotiate-time":
        return { data: this.controller.getMarketPeriodResponse() };
      case "notices":
        return { data: { cycleId: "tutorial", settlementMode: "prepaid", notices: [] } };
      case "org-status":
        return { data: this.controller.getOrgStatusResponse() };
      case "offensive-competition":
        return { data: this.controller.getCompetitionResponse() };
      case "hall-posts":
        return { data: this.controller.getHallResponse() };
      case "org-ranking": {
        const page = Number((config?.params as any)?.page ?? 0);
        if (page > 0) {
          return { data: [] };
        }
        return { data: this.controller.getRankingResponse() };
      }
      case "cycle-history":
        return { data: this.controller.getHistoryResponse() };
      default:
        return { data: {} };
    }
  }

  async post(url: string, data?: any, config?: AxiosRequestConfig) {
    const normalized = url.replace(/^\//, "");
    if (normalized === "bid") {
      this.controller.recordBidOnce(
        data?.postId,
        Number(data?.lastBidAmount) || 0,
        Number(data?.lastBidDiff) || 0,
        config?.headers?.["Idempotency-Key"] as string | undefined,
      );
      // ads-api answers a landed bid with a JSON null: the amount reaches the
      // page through addNewBid and the board reread that follows.
      return { data: null, status: 200 };
    }
    return { data: {} };
  }

  async patch(url: string, data?: any) {
    const normalized = url.replace(/^\//, "");
    if (normalized.startsWith("open/")) {
      const postId = normalized.split("/")[1];
      this.controller.openPost(postId);
      return { data: {} };
    }

    if (normalized === "sponsor-minor-audience") {
      return { data: this.controller.setSponsorMinorAudience(data?.enabled === true) };
    }
    if (normalized === "change-autobuy") {
      return { data: this.controller.setAutoBuy(Boolean(data?.autoBuy)) };
    }

    return { data: {} };
  }

  // Required stubs for AxiosInstance compatibility.
  request(): Promise<any> {
    return Promise.resolve({ data: {} });
  }
  delete(): Promise<any> {
    return Promise.resolve({ data: {} });
  }
  head(): Promise<any> {
    return Promise.resolve({ data: {} });
  }
  options(): Promise<any> {
    return Promise.resolve({ data: {} });
  }
  put(): Promise<any> {
    return Promise.resolve({ data: {} });
  }
  patchRequest(): Promise<any> {
    return Promise.resolve({ data: {} });
  }
}

const controller = new MockNegotiationController(mockCardSeeds, mockSessionInfo);
const mockRTA = new MockRTA(controller);
const mockApiInstance = new MockNegotiateAPI(controller);

const realApiInstance = axios.create({ baseURL: cfg.REACT_APP_API_NEGOTIATE_URL });

// The enterprise RTA relay is proxied at REACT_APP_API_RTA_SOCKET_PATH by the
// SAME host that serves the SPA: in prod, enterprise.dakasa.me/rta/socket.io via
// the host-ingress; in an ephemeral env, <eco>.staging.dakasa.me/rta via the
// per-env fan-out IngressRoute (/rta -> enterprise-rta). So window.location.origin
// is the correct WS host EVERYWHERE the SPA runs — prod, an eco, and dev. The
// build-time REACT_APP_API_RTA_URL bakes https://enterprise.dakasa.me, which is
// right in prod only by coincidence (== origin) and pointed an eco's socket at
// PROD. Preferring origin keeps the socket (and its JWT auth) on the SPA's own
// host in every env; the configured URL is a fallback only OUTSIDE a browser
// (SSR/tests). The socket PATH stays from config (same in all envs).
const resolveRtaUrl = (): string =>
  typeof window !== "undefined" ? window.location.origin : cfg.REACT_APP_API_RTA_URL;

const realSocket = io(resolveRtaUrl(), {
  transports: ["websocket"],
  path: cfg.REACT_APP_API_RTA_SOCKET_PATH,
  autoConnect: false,
  auth: async (cb) => {
    const token = ((await localforage.getItem("JWT")) as string) || "";
    cb({ token });
  },
});

let currentMode = defaultMockMode;
let modeRequest = 0;
let modeRevision = 0;
let modeReady = true;
let pendingMode: boolean | undefined;
let modeError: string | undefined;
const modeChangeListeners = new Set<() => void>();
let attachedRTA: NegotiationRTA = currentMode ? mockRTA : realSocket;
const listenerRegistry = new Map<string, Set<(...args: any[]) => void>>();

/**
 * Make the given socket session join its org room.
 *
 * The enterprise RTA backend keys org-scoped broadcasts on a Socket.IO room
 * named after the org id, which a session only joins after it emits the
 * `tagUserOrg` event with its `org_id`. Historically only the Átrio
 * (`negotiate.tsx`) emitted this, so sessions opened on any other page never
 * joined their room. Emitting it from the socket's own `connect` handler means
 * every authenticated session joins on connect AND re-joins on every
 * reconnect, regardless of which page opened the socket.
 *
 * Resolves to `true` when the room-join was emitted, `false` when there is no
 * usable session (logged-out / malformed token) so nothing is emitted.
 */
export async function joinOrgRoom(target: NegotiationRTA): Promise<boolean> {
  const info = await sessionInfo();
  if (!info || !info.org_id) return false;
  target.emit("tagUserOrg", info.org_id);
  return true;
}

/**
 * Listeners notified when the real socket comes back after having been away.
 *
 * A page that derives state from RTA pushes (the Átrio's market window, the
 * hall board) has no way to know what it missed while the socket was down. This
 * is the signal to re-read the authoritative HTTP state after an outage.
 */
const rtaReconnectListeners = new Set<() => void>();

/**
 * Whether the real socket has already completed its FIRST handshake.
 *
 * Socket.IO fires `connect` for the initial handshake as well as for every
 * automatic reconnect. Only the latter means "you may have missed events", so
 * the first one is swallowed — otherwise every fresh page load would fire a
 * redundant resync on top of its own mount fetch.
 */
let realSocketHandshakeSeen = false;

/**
 * Subscribe to real-socket RECONNECTS (never the initial handshake).
 *
 * Returns an unsubscribe function suitable for a `useEffect` cleanup.
 *
 * NOTE: this rides `realSocket` only. In mock mode the bridge swaps to
 * `mockRTA`, which never emits `connect`, so this never fires under the mocked
 * Playwright project — the poll/visibility paths are what keep that env honest.
 */
export function onRTAReconnect(listener: () => void): () => void {
  rtaReconnectListeners.add(listener);
  return () => {
    rtaReconnectListeners.delete(listener);
  };
}

// Re-join the org room on every (re)connection of the real socket. Socket.IO
// emits `connect` on the initial handshake and after each automatic reconnect,
// so this is the single central point that guarantees room membership for all
// pages. The Átrio keeps its own explicit emit (belt-and-suspenders for the
// pre-connect-handler race on first load).
realSocket.on("connect", () => {
  void joinOrgRoom(realSocket);

  if (!realSocketHandshakeSeen) {
    realSocketHandshakeSeen = true;
    return;
  }

  rtaReconnectListeners.forEach((listener) => {
    // One throwing subscriber must not stop the others from resyncing.
    try {
      listener();
    } catch (error) {
      console.error("RTA reconnect listener failed:", error);
    }
  });
});

function rebindRTA() {
  const target = currentMode ? mockRTA : realSocket;
  if (attachedRTA === target) return;
  listenerRegistry.forEach((listeners, event) => {
    listeners.forEach((listener) => {
      attachedRTA.off(event, listener);
      target.on(event, listener);
    });
  });
  attachedRTA = target;
}

interface MockModeMetadata {
  orgAvatar?: string;
  orgName?: string;
  session?: MockSessionInfo;
}

function notifyModeChange() {
  modeChangeListeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.error("Negotiation mode listener failed:", error);
    }
  });
}

export async function setMockNegotiationMode(enabled?: boolean, metadata?: MockModeMetadata) {
  const request = ++modeRequest;
  const desiredMode = typeof enabled === "boolean" ? enabled : defaultMockMode;
  const transitioning = desiredMode !== currentMode || !modeReady;
  if (transitioning) {
    const invalidated = modeReady || pendingMode !== desiredMode || modeError !== undefined;
    modeReady = false;
    pendingMode = desiredMode;
    modeError = undefined;
    if (invalidated) {
      // Close admission at the request, before metadata's first async read.
      // The old transport can still serve frozen intents; no new controls may
      // be admitted until the destination session and source are initialized.
      modeRevision += 1;
      notifyModeChange();
    }
  }
  // The tutorial runs under the collaborator's JWT. Defaulting every metadata
  // refresh to mock-org makes its bids belong to a different company than the
  // page's sessionInfo(), so the local total stays at zero and self-bid checks
  // never see the company as leading.
  let session: MockSessionInfo | undefined;
  try {
    const candidate = metadata?.session ?? (desiredMode ? await sessionInfo() : undefined);
    if (desiredMode) {
      // Missing/malformed tokens can only initialize the explicitly requested
      // mock. The financial helper still binds real actions to actual claims.
      session =
        candidate &&
        typeof candidate.iss === "string" &&
        candidate.iss !== "" &&
        typeof candidate.org_id === "string" &&
        candidate.org_id !== ""
          ? { iss: candidate.iss, org_id: candidate.org_id, exp: candidate.exp }
          : mockSessionInfo;
    } else if (candidate) session = candidate;
  } catch {
    if (request !== modeRequest) return currentMode;
    if (transitioning) {
      modeError = "Não foi possível iniciar o tutorial. Feche-o e tente novamente.";
      modeRevision += 1;
      notifyModeChange();
    }
    return currentMode;
  }
  if (request !== modeRequest) return currentMode;
  if (session) controller.setSessionInfo(session);
  const orgId = session?.org_id ?? mockSessionInfo.org_id;

  if (metadata?.orgAvatar) {
    mockOrgs["mock-org"] = { id: "mock-org", avatar: metadata.orgAvatar };
    mockOrgs[orgId] = {
      id: orgId,
      avatar: metadata.orgAvatar,
    };
  }
  mockOrgMetadata.avatar =
    mockOrgs[orgId]?.avatar || mockOrgs["mock-org"]?.avatar || mockOrgMetadata.avatar;
  if (metadata?.orgName) {
    mockOrgMetadata.name = metadata.orgName;
  }

  if (!transitioning) {
    return currentMode;
  }
  const changed = currentMode !== desiredMode;
  currentMode = desiredMode;
  rebindRTA();
  if (currentMode && changed) {
    controller.reset();
    mockRTA.connect();
    controller.setMarketPeriod("closed");
  }
  modeReady = true;
  pendingMode = undefined;
  modeError = undefined;
  modeRevision += 1;
  notifyModeChange();
  return currentMode;
}

export function isMockNegotiation() {
  return currentMode;
}

export function getNegotiationModeRevision() {
  return modeRevision;
}

export function isNegotiationModeReady() {
  return modeReady;
}

export function getNegotiationModeError() {
  return modeError;
}

/** A transport boundary change, independent of the real socket's reconnects. */
export function onNegotiationModeChange(listener: () => void): () => void {
  modeChangeListeners.add(listener);
  return () => {
    modeChangeListeners.delete(listener);
  };
}

export const negotiateAPI: AxiosInstance = new Proxy({} as AxiosInstance, {
  get(_, prop: keyof AxiosInstance) {
    const target = currentMode ? mockApiInstance : realApiInstance;
    const value = (target as any)[prop];
    if (typeof value === "function") {
      return value.bind(target);
    }
    return value;
  },
});

if (currentMode) {
  mockRTA.connect();
}

export const RTA: NegotiationRTA = {
  on(event: string, listener: (...args: any[]) => void) {
    if (!listenerRegistry.has(event)) {
      listenerRegistry.set(event, new Set());
    }
    listenerRegistry.get(event)?.add(listener);
    attachedRTA.on(event, listener);
  },
  off(event: string, listener: (...args: any[]) => void) {
    listenerRegistry.get(event)?.delete(listener);
    attachedRTA.off(event, listener);
  },
  emit(event: string, ...payload: any[]) {
    attachedRTA.emit(event, ...payload);
  },
  connect() {
    attachedRTA.connect();
  },
  disconnect() {
    attachedRTA.disconnect();
  },
};

export const sessionInfo = async () => {
  const token = (await localforage.getItem("JWT")) as string | undefined;
  if (!token) return currentMode ? mockSessionInfo : false;
  try {
    return JSON.parse(window.atob(token.split(".")[1]));
  } catch (error) {
    return currentMode ? mockSessionInfo : false;
  }
};

export const mockNegotiationControl = {
  emitBid(postId?: string, overrides: Partial<MockCard> = {}) {
    return controller.emitNewBid(postId, overrides);
  },
  setAutoBuy(enabled: boolean) {
    return controller.setAutoBuy(enabled);
  },
  setHistory(data: typeof mockHistorySeeds) {
    controller.setHistory(data);
  },
  setRanking(data: typeof mockRankingSeeds) {
    controller.setRanking(data);
  },
  openPost(postId: string) {
    controller.openPost(postId);
  },
  reset() {
    controller.reset();
  },
  setSponsorMinorAudience(enabled: boolean) {
    return controller.setSponsorMinorAudience(enabled);
  },
  setMarketPeriod(period: NegotiationPeriod["period"], endAt?: string) {
    return controller.setMarketPeriod(period, endAt);
  },
};

setMockNegotiationMode().catch(() => {});
